import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { SparkFlow } from '@/features/spark/SparkFlow';
import { useSparkStore } from '@/features/spark/store';
import type { DomainType } from '@/api/types';
import { lookupProductPrefill } from '@/data/productPrefills';
import { lookupStarterPrefill } from '@/data/starterPrefills';

/** Optional location-state shape passed by Marketplace's "Use this
 *  product" button. Pre-fills Step 1 (name + description + domain)
 *  so the founder lands one click into a ready-to-customize Spark. */
interface SparkPrefill {
  name?: string;
  description?: string;
  domain?: DomainType;
}

interface SparkLocationState {
  prefill?: SparkPrefill;
}

export function SparkPage() {
  const location = useLocation();
  const [search] = useSearchParams();
  const applyStep1 = useSparkStore((s) => s.applyStep1);
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    // Source 1: explicit location.state from intra-app links
    // (Marketplace's "Use this product" button).
    const state = location.state as SparkLocationState | null;
    let prefill: SparkPrefill | undefined = state?.prefill;

    // Source 2: ?product=<slug> query param from the marketing site's
    // "Start Using" CTA. The website lives off-domain
    // (growing-intelligence.com → app.growing-intelligence.com), so
    // location.state isn't available — the product slug travels in
    // the URL instead.
    if (!prefill) {
      const productSlug = search.get('product');
      if (productSlug) {
        const lookup = lookupProductPrefill(productSlug);
        if (lookup) {
          prefill = {
            name: lookup.name,
            description: lookup.description,
            domain: lookup.domain,
          };
        }
      }
    }

    // Source 3: ?starter=<slug> query param from the marketing site's
    // segment cards (one per starter recipe — 65 across the 13 ready
    // segments). The slug is kebab-case (`?starter=achievement-proof`);
    // the lookup normalizes to the segments.ts/segments.json data
    // shape and inherits the segment's engine domain.
    if (!prefill) {
      const starterSlug = search.get('starter');
      if (starterSlug) {
        const lookup = lookupStarterPrefill(starterSlug);
        if (lookup) {
          prefill = {
            name: lookup.name,
            description: lookup.description,
            domain: lookup.domain,
          };
        }
      }
    }

    if (!prefill) return;
    consumed.current = true;
    const current = useSparkStore.getState();
    // Step 1's domain field accepts only the standard string variants
    // (`'Audio' | 'Process' | …`). Custom-envelope domains
    // (Cyber / Education / Legal) and the store's potentially-Custom
    // current value both fall back to `'Generic'` rather than
    // crashing the zod schema.
    const standardDomains = [
      'Audio',
      'Process',
      'Medical',
      'Financial',
      'Defense',
      'Government',
      'Generic',
    ] as const;
    type StandardDomain = (typeof standardDomains)[number];
    const isStandard = (d: unknown): d is StandardDomain =>
      typeof d === 'string' && (standardDomains as readonly string[]).includes(d);
    const domain: StandardDomain = isStandard(prefill.domain)
      ? prefill.domain
      : isStandard(current.domain)
        ? current.domain
        : 'Generic';
    applyStep1({
      name: prefill.name ?? current.name,
      description: prefill.description ?? current.description,
      version: current.version,
      domain,
      author_name: current.author_name,
    });
  }, [applyStep1, location.state, search]);

  return <SparkFlow />;
}
